'use client';

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/src/shared/lib/shadcn';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** A shorter label for narrow widths, shown below sm. */
  shortLabel?: string;
  icon?: LucideIcon;
}

const HEIGHT = { sm: 'h-9', md: 'h-10', lg: 'h-11' } as const;

interface SegmentedControlProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  options: readonly SegmentedOption<T>[];
  'aria-label': string;
  'aria-describedby'?: string;
  size?: keyof typeof HEIGHT;
  disabled?: boolean;
  className?: string;
}

/**
 * Mutually exclusive options drawn as one track. A radio group to assistive
 * tech, so the arrow keys move the selection and only the checked segment is
 * in the tab order.
 */
export function SegmentedControl<T extends string>({
  value,
  onValueChange,
  options,
  size = 'md',
  disabled,
  className,
  ...aria
}: SegmentedControlProps<T>) {
  return (
    <RadioGroupPrimitive.Root
      value={value}
      onValueChange={(next) => onValueChange(next as T)}
      orientation="horizontal"
      disabled={disabled}
      aria-label={aria['aria-label']}
      aria-describedby={aria['aria-describedby']}
      className={cn(
        'grid auto-cols-fr grid-flow-col gap-1 rounded-xl bg-(--sc-subtle) p-1',
        HEIGHT[size],
        className,
      )}
    >
      {options.map(({ value: optionValue, label, shortLabel, icon: Icon }) => (
        <RadioGroupPrimitive.Item
          key={optionValue}
          value={optionValue}
          aria-label={shortLabel ? label : undefined}
          className={cn(
            'flex min-w-0 cursor-pointer items-center justify-center gap-1.5 rounded-[9px] px-2 text-sm font-medium whitespace-nowrap text-muted-foreground outline-none',
            'hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-solid focus-visible:outline-(--sc-ring)',
            'data-[state=checked]:bg-(--sc-card) data-[state=checked]:font-semibold data-[state=checked]:text-foreground data-[state=checked]:shadow-(--sc-shadow-rest)',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          {Icon && <Icon aria-hidden className="size-4 shrink-0" />}
          {shortLabel ? (
            <>
              <span className="truncate sm:hidden">{shortLabel}</span>
              <span className="truncate max-sm:hidden">{label}</span>
            </>
          ) : (
            <span className="truncate">{label}</span>
          )}
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  );
}
